import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import axiosInstance from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const MAX_FILE_BYTES = 500 * 1024; // 500KB

/* ---------------- Small UI helpers ---------------- */
const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${className}`}
  >
    {children}
  </span>
);

const ModalShell = ({ title, onClose, children }) => {
  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute inset-0 flex items-center justify-center p-3">
        <div className="w-full max-w-lg bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900">{title}</div>
            <button
              className="p-1 rounded hover:bg-gray-100"
              onClick={onClose}
              type="button"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Main Page ---------------- */
function BannerManagement() {
  const { user: currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [activeTab, setActiveTab] = useState("banner"); // "banner" | "spotlight"

  // lists
  const [banners, setBanners] = useState([]);
  const [spotlights, setSpotlights] = useState([]);

  // loading + errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // modals
  const [openBannerModal, setOpenBannerModal] = useState(false);
  const [openSpotlightModal, setOpenSpotlightModal] = useState(false);

  // Targets
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);

  const handleMenuToggle = () => setSidebarOpen((p) => !p);
  const handleSidebarClose = () => setSidebarOpen(false);

  const handleAddClick = () => {
    if (activeTab === "banner") setOpenBannerModal(true);
    else setOpenSpotlightModal(true);
  };

  // if you use auth, add it here
  const headers = useMemo(() => {
    return {};
  }, []);

  const fetchTargets = async () => {
    try {
      const [tRes, pRes] = await Promise.allSettled([
        axiosInstance.get(`${API_BASE}/api/tests`, { headers, params: { limit: 200 } }),
        axiosInstance.get(`${API_BASE}/api/checkups`, {
          headers,
          params: { limit: 200 },
        }),
      ]);

      if (tRes.status === "fulfilled") {
        const data = tRes.value.data;
        setTests(data.tests || data.data || data || []);
      }
      if (pRes.status === "fulfilled") {
        const data = pRes.value.data;
        setPackages(data.packages || data.data || data || []);
      }
    } catch (e) {
      console.warn("Failed to fetch tests/packages:", e);
    }
  };

  const fetchBanners = async () => {
    const res = await axiosInstance.get(`${API_BASE}/api/banners?includeArchived=true`, {
      headers,
      withCredentials: true,
    });
    setBanners(res.data.banners || res.data || []);
  };

  const fetchSpotlights = async () => {
    const res = await axiosInstance.get(`${API_BASE}/api/spotlights?includeArchived=true`, {
      headers,
      withCredentials: true,
    });
    setSpotlights(res.data.spotlights || res.data || []);
  };

  const refresh = async () => {
    try {
      setLoading(true);
      setError("");
      await Promise.all([fetchBanners(), fetchSpotlights()]);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Failed to load data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Delete actions ---------------- */
  const deleteBanner = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/banners/${id}`, {
        headers,
        withCredentials: true,
      });
      setBanners((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to delete banner");
    }
  };

  const deleteSpotlight = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/spotlights/${id}`, {
        headers,
        withCredentials: true,
      });
      setSpotlights((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to delete spotlight");
    }
  };

  /* ---------------- Render ---------------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                Banner Management
              </h1>
              <p className="text-xs text-gray-600">
                Manage banners and spotlight banners shown in the app.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={refresh}
                className="text-xs px-3 py-2"
              >
                <Icon name="RefreshCcw" size={14} className="mr-1" />
                Refresh
              </Button>

              <Button
                onClick={handleAddClick}
                disabled={!canAccess(currentUser, "canCreate")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="Plus" size={14} className="mr-1" />
                {activeTab === "banner" ? "Add Banner" : "Add Spotlight"}
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border border-gray-200 rounded-lg p-2 inline-flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setActiveTab("banner")}
              className={`px-3 py-2 rounded-md text-xs font-medium transition ${
                activeTab === "banner"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Banner
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("spotlight")}
              className={`px-3 py-2 rounded-md text-xs font-medium transition ${
                activeTab === "spotlight"
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              Spotlight
            </button>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-900">
                {activeTab === "banner" ? "Banners" : "Spotlight Banners"}
              </div>

              {loading ? (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  Loading...
                </div>
              ) : (
                <div className="text-xs text-gray-600">
                  {activeTab === "banner"
                    ? banners.length
                    : spotlights.length}{" "}
                  items
                </div>
              )}
            </div>

            {error ? (
              <div className="p-4">
                <div className="flex items-start gap-2 text-red-600 text-xs">
                  <Icon name="AlertCircle" size={16} />
                  <div>
                    <div className="font-semibold">Error</div>
                    <div className="text-red-500">{error}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {activeTab === "banner" ? (
                  <table className="w-full text-xs">
                    <thead className="bg-white border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Image
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Target
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {banners.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-6 text-center text-gray-500"
                          >
                            No banners found.
                          </td>
                        </tr>
                      ) : (
                        banners.map((b) => (
                          <tr key={b.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={b.imgUrl}
                                  alt="banner"
                                  className="h-10 w-16 rounded object-cover border"
                                />
                                <div className="text-gray-600">#{b.id}</div>
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              {b.testId ? (
                                <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                                  Test: {b.test?.name || b.testId}
                                </Badge>
                              ) : b.packageId ? (
                                <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                                  Package: {b.package?.name || b.packageId}
                                </Badge>
                              ) : (
                                <span className="text-gray-400">No target</span>
                              )}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!canAccess(currentUser, "canDelete")}
                                  className="text-xs px-3 py-1 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => canAccess(currentUser, "canDelete") && deleteBanner(b.id)}
                                >
                                  <Icon
                                    name="Trash2"
                                    size={14}
                                    className="mr-1"
                                  />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-white border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Image
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Show In
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">
                          Target
                        </th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {spotlights.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-6 text-center text-gray-500"
                          >
                            No spotlights found.
                          </td>
                        </tr>
                      ) : (
                        spotlights.map((s) => (
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <img
                                  src={s.imgUrl}
                                  alt="spotlight"
                                  className="h-10 w-16 rounded object-cover border"
                                />
                                <div className="text-gray-600">#{s.id}</div>
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(s.showIn || []).map((p) => (
                                  <Badge
                                    key={p}
                                    className="bg-gray-50 text-gray-700 border-gray-200"
                                  >
                                    {p}
                                  </Badge>
                                ))}
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              {s.testId ? (
                                <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                                  Test: {s.test?.name || s.testId}
                                </Badge>
                              ) : s.packageId ? (
                                <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                                  Package: {s.package?.name || s.packageId}
                                </Badge>
                              ) : (
                                <span className="text-gray-400">No target</span>
                              )}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={!canAccess(currentUser, "canDelete")}
                                  className="text-xs px-3 py-1 border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => canAccess(currentUser, "canDelete") && deleteSpotlight(s.id)}
                                >
                                  <Icon
                                    name="Trash2"
                                    size={14}
                                    className="mr-1"
                                  />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {openBannerModal && (
        <BannerModal
          tests={tests}
          packages={packages}
          onClose={() => setOpenBannerModal(false)}
          onCreated={async () => {
            setOpenBannerModal(false);
            await fetchBanners();
          }}
        />
      )}

      {openSpotlightModal && (
        <SpotlightModal
          tests={tests}
          packages={packages}
          onClose={() => setOpenSpotlightModal(false)}
          onCreated={async () => {
            setOpenSpotlightModal(false);
            await fetchSpotlights();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Banner Modal (target optional) ---------------- */
function BannerModal({ tests, packages, onClose, onCreated }) {
  const [targetType, setTargetType] = useState("test"); // "test" | "package"
  const [testId, setTestId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [file, setFile] = useState(null);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = (f) => {
    setErr("");
    if (!f) return setFile(null);

    if (f.size > MAX_FILE_BYTES) {
      setFile(null);
      setErr("Image must be within 500KB.");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!file) return setErr("Image is required (<= 500KB).");

    const fd = new FormData();
    fd.append("image", file);

    // ✅ target optional: append only if selected
    if (targetType === "test" && testId) fd.append("testId", testId);
    if (targetType === "package" && packageId) fd.append("packageId", packageId);

    try {
      setSaving(true);
      await axios.post(`${API_BASE}/api/banners`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onCreated();
    } catch (e2) {
      setErr(
        e2?.response?.data?.error ||
          e2?.response?.data?.message ||
          "Failed to create banner."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Add Banner" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {err}
          </div>
        )}

        {/* Target Type */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Target Type (optional)
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTargetType("test")}
              className={`px-3 py-2 rounded-md text-xs border ${
                targetType === "test"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              Test
            </button>
            <button
              type="button"
              onClick={() => setTargetType("package")}
              className={`px-3 py-2 rounded-md text-xs border ${
                targetType === "package"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              Package
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            If you don’t select a target, the banner will be created without
            linking to any test/package.
          </p>
        </div>

        {/* Target Select */}
        {targetType === "test" ? (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Test (optional)
            </label>
            <select
              value={testId}
              onChange={(e) => setTestId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">Select...</option>
              {tests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Package (optional)
            </label>
            <select
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">Select...</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Image */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Banner Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
            className="w-full text-xs"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Required size: <b>330 × 220</b> • Max: <b>500KB</b> (JPG/PNG/WebP)
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
          >
            {saving ? "Saving..." : "Create"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

/* ---------------- Spotlight Modal (target optional) ---------------- */
function SpotlightModal({ tests, packages, onClose, onCreated }) {
  const [targetType, setTargetType] = useState("test"); // "test" | "package"
  const [testId, setTestId] = useState("");
  const [packageId, setPackageId] = useState("");
  const [file, setFile] = useState(null);

  // ✅ Single select (tab) for showIn
  const [showIn, setShowIn] = useState("HOME_MIDDLE");

  // ✅ 4 options (as per enum)
  const SHOW_IN_OPTIONS = useMemo(
    () => ["HOME_MIDDLE", "HOME_END", "TEST_PAGE", "CHECKUP_PAGE"],
    []
  );

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // size hints based on placement
  const sizeHint = useMemo(() => {
    if (showIn === "HOME_END") return "330 × 500";
    return "330 × 220";
  }, [showIn]);

  const handleFile = (f) => {
    try {
      setErr("");
      if (!f) return setFile(null);

      if (f.size > MAX_FILE_BYTES) {
        setFile(null);
        setErr("Image must be within 500KB.");
        return;
      }
      setFile(f);
    } catch (e) {
      console.error(e);
      setErr("Failed to read file. Try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();

    try {
      setErr("");

      if (!file) return setErr("Image is required (<= 500KB).");
      if (!showIn) return setErr("Please select showIn.");

      const fd = new FormData();
      fd.append("image", file);

      // backend expects array
      fd.append("showIn", JSON.stringify([showIn]));

      // ✅ target optional: append only if selected
      if (targetType === "test" && testId) fd.append("testId", testId);
      if (targetType === "package" && packageId)
        fd.append("packageId", packageId);

      setSaving(true);
      await axios.post(`${API_BASE}/api/spotlights`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      await onCreated();
    } catch (e2) {
      console.error(e2);
      setErr(
        e2?.response?.data?.error ||
          e2?.response?.data?.message ||
          "Failed to create spotlight."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="Add Spotlight Banner" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {err}
          </div>
        )}

        {/* ShowIn tabs (4) */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Show In (select one)
          </label>

          <div className="flex flex-wrap gap-2">
            {SHOW_IN_OPTIONS.map((opt) => {
              const active = showIn === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setShowIn(opt)}
                  className={`px-3 py-2 rounded-md text-xs border transition ${
                    active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <p className="text-[11px] text-gray-500 mt-1">
            Selected: <b>{showIn}</b> • Required size: <b>{sizeHint}</b>
          </p>
        </div>

        {/* Target Type */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Target Type (optional)
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTargetType("test")}
              className={`px-3 py-2 rounded-md text-xs border ${
                targetType === "test"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              Test
            </button>

            <button
              type="button"
              onClick={() => setTargetType("package")}
              className={`px-3 py-2 rounded-md text-xs border ${
                targetType === "package"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              Package
            </button>
          </div>

          <p className="text-[11px] text-gray-500 mt-1">
            You can create spotlight without a target. If selected, it will link
            to that test/package.
          </p>
        </div>

        {/* Target Select */}
        {targetType === "test" ? (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Test (optional)
            </label>
            <select
              value={testId}
              onChange={(e) => setTestId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">Select...</option>
              {tests.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Select Package (optional)
            </label>
            <select
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">Select...</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Image */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Spotlight Image
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
            className="w-full text-xs"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Required size: <b>{sizeHint}</b> • Max: <b>500KB</b> (JPG/PNG/WebP)
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
          >
            {saving ? "Saving..." : "Create"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export default BannerManagement;
